/**
 * IncomeExpenseTrend — a simple two-series bar chart comparing money coming in
 * (income) vs money going out (expenses) across buckets of the selected period,
 * for the Accounts Overview. The buckets are computed by the caller from the
 * real recent-activity feed (weekly for a month, monthly for longer spans), so
 * no new backend series is invented.
 *
 * Values are major-unit numbers (already ÷100 by the caller). Client-only
 * (console bundle, never SSR).
 */
import type { ReactElement } from "react";
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
} from "recharts";

const FOREST = "#1D35A9";
const CLAY = "#B42318";
const RULE = "#EAECF0";
const SLATE = "#667085";

/** One bucket on the trend: a label plus income + expense major-unit totals. */
export interface TrendBucket {
  readonly label: string;
  readonly income: number;
  readonly expense: number;
}

function formatCompact(major: number): string {
  if (major >= 10_000_000) return `₹${(major / 10_000_000).toFixed(1)}Cr`;
  if (major >= 100_000) return `₹${(major / 100_000).toFixed(1)}L`;
  if (major >= 1_000) return `₹${(major / 1_000).toFixed(0)}k`;
  return `₹${major.toFixed(0)}`;
}

function formatFull(major: number): string {
  try {
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      maximumFractionDigits: 0,
    }).format(major);
  } catch {
    return `₹${major.toFixed(0)}`;
  }
}

function TrendTooltip({
  active,
  payload,
  label,
}: {
  readonly active?: boolean;
  readonly payload?: readonly { readonly dataKey?: string; readonly value?: number }[];
  readonly label?: string;
}): ReactElement | null {
  if (active !== true || payload === undefined || payload.length === 0) {
    return null;
  }
  const inc = payload.find((p) => p.dataKey === "income")?.value ?? 0;
  const exp = payload.find((p) => p.dataKey === "expense")?.value ?? 0;
  return (
    <div className="chart-tooltip">
      <p className="chart-tooltip__title">{label}</p>
      <p className="chart-tooltip__row">
        <span className="chart-tooltip__swatch" style={{ background: FOREST }} />
        Income: <strong>{formatFull(inc)}</strong>
      </p>
      <p className="chart-tooltip__row">
        <span className="chart-tooltip__swatch" style={{ background: CLAY }} />
        Expenses: <strong>{formatFull(exp)}</strong>
      </p>
    </div>
  );
}

/** The income-vs-expenses grouped bar chart. */
export function IncomeExpenseTrend({
  buckets,
}: {
  readonly buckets: readonly TrendBucket[];
}): ReactElement {
  const hasAny = buckets.some((b) => b.income > 0 || b.expense > 0);
  if (buckets.length === 0 || !hasAny) {
    return (
      <p className="console-empty">
        No income or expense activity for this period yet.
      </p>
    );
  }

  return (
    <div className="chart-frame" style={{ height: 260 }}>
      <ResponsiveContainer width="100%" height="100%">
        <BarChart
          data={buckets as TrendBucket[]}
          margin={{ top: 8, right: 12, left: 4, bottom: 4 }}
          barGap={4}
        >
          <CartesianGrid stroke={RULE} vertical={false} />
          <XAxis
            dataKey="label"
            tick={{ fill: SLATE, fontSize: 12 }}
            axisLine={{ stroke: RULE }}
            tickLine={false}
          />
          <YAxis
            tickFormatter={formatCompact}
            tick={{ fill: SLATE, fontSize: 12 }}
            axisLine={false}
            tickLine={false}
            width={56}
          />
          <Tooltip content={<TrendTooltip />} cursor={{ fill: "rgba(20,64,47,0.04)" }} />
          <Legend
            verticalAlign="top"
            align="right"
            iconType="circle"
            wrapperStyle={{ fontSize: 12, color: SLATE }}
          />
          <Bar name="Income" dataKey="income" fill={FOREST} radius={[4, 4, 0, 0]} maxBarSize={26} />
          <Bar name="Expenses" dataKey="expense" fill={CLAY} radius={[4, 4, 0, 0]} maxBarSize={26} />
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}

export default IncomeExpenseTrend;
